<template>
  <span
    class="main-wrapper"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @click="clickTitle"
    :style="bulletStyle"
  >
    <span class="info-icon">{{ label }}</span>
    <InfoTooltipContent
      v-if="isTooltipVisible"
      :id="id"
      :title="title"
      :page="page"
      :snippet="snippet"
      :thumb-url="thumbUrl"
      @title-clicked="clickTitle"
      class="tooltip-container"
    />
  </span>
</template>

<script>
import { computed, onMounted, ref } from 'vue';

import { decodeFromBase64 } from '~/utils/message';
import { thumbnailCache } from '~/services/thumbnail-cache-service';
import InfoTooltipContent from '~/components/conversation/chatMessage/InfoTooltipContent.vue';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';

export default {
  name: 'InfoTooltip',
  components: { InfoTooltipContent },
  emits: ['titleClicked'],
  props: {
    id: {
      type: String,
      default: '',
    },
    snippetIndex: {
      type: String,
      default: '',
    },
    messageData: {
      type: Object,
      default: () => {},
    },
    page: {
      type: String,
      default: '',
    },

    label: {
      type: Number,
      default: 1,
    },

    service: {
      type: Object,
      default: () => {},
    },
    top: {
      type: Number,
      default: 0,
    },
    left: {
      type: Number,
      default: 0,
    },
  },

  setup(props, { emit }) {
    const isTooltipVisible = ref(false);
    const thumbUrl = ref('');
    const currentReference = ref({});

    const currentIndex = computed(
      () => (props.messageData?.data?.[0]?.references || []).findIndex((ref) => ref.externalId === props.id) + 1,
    );

    const title = computed(() => currentReference.value?.name || '');

    const snippet = computed(() => {
      if (!currentReference.value?.snippets) {
        return '';
      }
      const specificSnippet = currentReference.value.snippets[`${props.page}-${props.snippetIndex}`];
      return '...' + decodeFromBase64(specificSnippet || currentReference.value?.snippet || '');
    });

    const bulletStyle = computed(() =>
      props.top && props.left
        ? {
            position: 'absolute',
            top: `${props.top}px`,
            left: `${props.left}px`,
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: '2px solid #6222e3',
            color: '#6222e3',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '12px',
            zIndex: 10000,
            cursor: 'default',
          }
        : {},
    );

    const loadPageCover = async () => {
      // Try to get from cache first
      const cachedUrl = thumbnailCache.get(props.id, props.page);
      if (cachedUrl) {
        thumbUrl.value = cachedUrl;
        return;
      }

      try {
        const { data } = await props.service.post(apiGatewayEndpoints.s3Docs.pageCovers, {
          documents: [{ id: props.id, pages: [props.page] }],
        });

        const url = (data && data[0]?.pages[props.page]?.small) || '';
        thumbUrl.value = url;

        // Cache the URL if we got one
        if (url) {
          thumbnailCache.set(props.id, props.page, url);
        }
      } catch (error) {
        thumbUrl.value = '';
      }
    };

    const showTooltip = async () => {
      currentReference.value = (props.messageData?.data?.[0]?.references || []).find(
        (ref) => ref.externalId === props.id,
      );
      isTooltipVisible.value = true;
      await loadPageCover();
    };

    // Preload the image when component mounts
    onMounted(() => {
      currentReference.value = (props.messageData?.data?.[0]?.references || []).find(
        (ref) => ref.externalId === props.id,
      );
      loadPageCover();
    });

    const hideTooltip = () => (isTooltipVisible.value = false);
    const clickTitle = () => emit('titleClicked', { reference: currentReference.value, page: Number(props.page) });

    return {
      currentIndex,
      currentReference,
      clickTitle,
      thumbUrl,
      isTooltipVisible,
      showTooltip,
      hideTooltip,
      title,
      snippet,
      bulletStyle,
    };
  },
};
</script>

<style scoped lang="scss">
.main-wrapper {
  position: relative;

  .info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 24px;
    height: 24px;
    background-color: $bb-neutral-0;
    border: 2px solid $bb-primary-3;
    border-radius: 50%;
    color: $bb-primary-3;
    box-sizing: border-box;
    margin-right: $bb-spacing-1;
  }

  .tooltip-container {
    position: absolute;
    left: 50%;
    transform: translateX(-10%);
    background-color: $bb-neutral-0;
    width: 300px; /* Adjust width */
    max-height: 200px; /* Maximum height */
    overflow: hidden;
    border: 1px solid #ddd;
    border-radius: $bb-spacing-2;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column; /* Stack rows */
    z-index: 1000;
  }
}
</style>
